/*
 * A panel for selecting map styles from streets, satellite, hybrid, monochrome
 */
import React, { useContext, useRef, useState } from "react";
import { Popover, Slider } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { MapStyle } from "../types";

import HybridImage from "./images/HybridImage.png";
import MapImage from "./images/MapImage.png";
import MonochromeImage from "./images/Monochrome.png";
import SatelliteImage from "./images/SatelliteImage.jpeg";
import { MapContext } from "./MapProvider";

import { Container, ImageContainer, Label, StyleImage, StyleOpacity, StyleOptionBox, StylePanel } from "./BaseMapStyleToggle.styles";

export const options = [
  {
    value: "map",
    text: "Streets",
    img: MapImage,
  },
  {
    value: "satellite",
    text: "Satellite",
    img: SatelliteImage,
  },
  {
    value: "hybrid",
    text: "Hybrid",
    img: HybridImage,
  },
  {
    value: "monochrome",
    text: "Mono",
    img: MonochromeImage,
  },
];

interface IProps {
  onChange: (style: MapStyle) => void;
}

const BaseMapStyleToggle: React.FC<IProps> = ({ onChange }: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverTriggerRef = useRef(false);
  const isOverPopoverRef = useRef(false);
  const { mapStyle: style, mapStyleOpacity, setMapStyleOpacity } = useContext(MapContext);
  const selectedOption = options.find((option) => option.value === style);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!isOverTriggerRef.current && !isOverPopoverRef.current) {
        setAnchorEl(null);
        closeTimeoutRef.current = null;
      }
    }, 1000);
  };

  const handleTriggerEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    isOverTriggerRef.current = true;
    clearCloseTimeout();
    if (!anchorEl) setAnchorEl(event.currentTarget);
  };

  const handleTriggerLeave = () => {
    isOverTriggerRef.current = false;
    scheduleClose();
  };

  const handlePopoverEnter = () => {
    isOverPopoverRef.current = true;
    clearCloseTimeout();
  };

  const handlePopoverLeave = () => {
    isOverPopoverRef.current = false;
    scheduleClose();
  };

  const handleClick = (optionStyle: string) => {
    if (optionStyle !== style) {
      onChange(optionStyle as MapStyle);
    }
  };

  return (
    <Container display="flex">
      <Box
        id="current"
        data-testid={`map-style-current-${style}`}
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={handleTriggerLeave}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <ImageContainer>
          <img src={selectedOption?.img} alt={`${style} View`} />
        </ImageContainer>
        <Label>
          <Typography variant="caption">{selectedOption?.text}</Typography>
        </Label>
      </Box>
      <Popover
        autoFocus={false}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: 100,
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: 0,
        }}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            pointerEvents: "auto",
          },
        }}
      >
        <StylePanel px={1} display="flex" alignItems="flex-start" flexDirection="column">
          <Box display="flex" flexDirection="row" alignItems="center">
            {options.map((option) => (
              <StyleOptionBox
                key={`map-style-${option.value}`}
                data-testid={`map-style-option-${option.value}`}
                onClick={() => handleClick(option.value)}
              >
                <StyleImage src={option.img} alt={`${option.text} View`} />
                <Box>
                  <Typography variant="caption" data-testid={`map-style-label-${option.value}`}>
                    {option.text}
                  </Typography>
                </Box>
              </StyleOptionBox>
            ))}
          </Box>
          <StyleOpacity>
            <Typography variant="caption" gutterBottom fontWeight={500}>
              Opacity {Math.round((mapStyleOpacity ?? 1) * 100)}%
            </Typography>
            <Slider
              sx={{ width: "100%" }}
              value={mapStyleOpacity}
              max={1}
              min={0}
              step={0.01}
              onChange={(_, newValue) => setMapStyleOpacity?.(newValue as number)}
            />
          </StyleOpacity>
        </StylePanel>
      </Popover>
    </Container>
  );
};

export default BaseMapStyleToggle;
